import moment from 'moment';
import { Internationalization } from '@syncfusion/ej2-base';
import { Timezone } from '@syncfusion/ej2-react-schedule';
import { Settings } from 'op2mise-react-widgets';
import DateService from 'core/date.service';

export const getEndOfMonth = (date = null) => {
  const targetDate = date ? new Date(date) : new Date();
  const lastDayOfMonth = new Date(
    targetDate.getFullYear(),
    targetDate.getMonth() + 1,
    0,
  );
  return lastDayOfMonth;
};

export const adjustDateByTimezoneOffset = (dateString, timezone) => {
  let offset = parseInt(timezone.replace('Etc/GMT', ''), 10) * -1;
  // Convert the date string to a Date object
  let date = new Date(dateString);

  // Add XX hours to the date
  if (offset < 0) {
    date.setHours(date.getHours() - offset);
  } else {
    date.setHours(date.getHours() + offset);
  }

  // Return the updated date
  return date;
};

export const adjustDateIfMidnight = (dateString) => {
  const dateObj = new Date(dateString);

  // Check if the hour is midnight (00:00:00)
  if (dateObj.getUTCHours() === 0) {
    // Add one day
    dateObj.setUTCDate(dateObj.getUTCDate() + 1);
  }

  // Convert the updated date back to the ISO string format
  // const adjustedDateString = dateObj.toISOString();

  return dateObj;
};

export const adjustTimeToMidnight = (dateString, period, timezone) => {
  const date = new Date(dateString);
  // Increment the date by 1 to get the next day
  if (period === 'start') {
    date.setDate(date.getDate());
  } else {
    date.setDate(date.getDate() + 1);
  }
  // Set time to 12:00 AM
  date.setHours(0, 0, 0, 0);
  return adjustDateByTimezoneOffset(date, timezone);
};

export const computeTimeDifference = (start, end) => {
  /**
   * Computes the time difference between two provided dates and returns a string representation of the difference.
   * The function accepts two Date objects (propDate1 and propDate2) and calculates the time elapsed between them.
   * The result is formatted as a descriptive string indicating the difference in years, months, days, hours, minutes, and seconds.
   */

  // Calculate the difference in milliseconds
  let timeDifference = Math.abs(end - start);

  // Convert milliseconds to hours and minutes
  let hours = Math.floor(timeDifference / (1000 * 60 * 60));
  let minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));

  // Format the duration
  let formattedHours = hours.toString().padStart(2, '0');
  let formattedMinutes = minutes.toString().padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}`;
};

export const formatDateWithMoment = (
  inputDate,
  format = 'YYYY-MM-DD',
  inputFormat,
) => {
  if (!inputDate) return '';
  const formattedDate = moment(inputDate).format(format);

  if (inputFormat) {
    return moment(inputDate, inputFormat).format(format);
  }

  return formattedDate;
};

export const subtractDaysToDate = (inputDate, daysToAdd) => {
  return moment(inputDate)
    .subtract(daysToAdd, 'days')
    .toDate();
};

export const getMondayOfTheWeek = (inputDate) => {
  const startOfWeek = moment(inputDate)
    .startOf('week')
    .toDate(); // Sunday
  return addDaysToDate(startOfWeek, 1); // Monday
};

export const addDaysToDate = (inputDate, daysToAdd) => {
  return moment(inputDate)
    .add(daysToAdd, 'days')
    .toDate();
};

export const getWeekNumber = (dateString, startDateString, endDateString) => {
  const date = moment(dateString);
  const startDate = moment(startDateString);
  const endDate = moment(endDateString);

  // Validate input dates
  if (!date.isValid() || !startDate.isValid() || !endDate.isValid()) {
    throw new Error('Invalid date format. Please use YYYY-MM-DD format.');
  }

  // Calculate week number
  const weekNumber = date.diff(startDate, 'weeks') + 1;

  // Check if the date is within the range
  if (weekNumber < 1 || date > endDate) {
    return null; // Outside of the specified date range
  }

  return weekNumber;
};

export const setToMidnight = (inputDate) => {
  /**
   * Sets the time of a given Date object to midnight (00:00:00).
   * @param {Date} inputDate - The Date object to modify.
   * @returns {Date} A new Date object with the time set to midnight.
   */
  let date = new Date(inputDate);
  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date;
};

export const getMonthFromDate = (dateString) => {
  /**
   * Retrieves the abbreviated month name ('JAN' format) from a given date string using Moment.js.
   * @param {string} dateString - The date string to parse. Should be in a format recognized by Moment.js.
   * @returns {string} The abbreviated month name ('JAN' format) of the provided date string.
   *                   Returns 'Invalid Date' if the date string is invalid.
   */
  const date = moment(dateString);

  if (!date.isValid()) {
    return 'Invalid Date';
  }

  return date.format('MMM').toUpperCase();
};

/**
 * Sets the seconds of a Date object to 00 and returns the modified Date object.
 *
 * @param {Date} dateValue - The original Date object.
 * @returns {Date} - A new Date object with seconds set to 00.
 */
export const setSecondsToZero = (dateValue) => {
  if (!(dateValue instanceof Date)) {
    throw new Error('The provided value must be a Date object.');
  }
  // Create a new Date object with seconds set to 00
  const newDate = new Date(dateValue.getTime());
  newDate.setSeconds(0);
  return newDate;
};

export const durationStringToMinutes = (timeString) => {
  /**
   * Converts a time duration string into minutes.
   * Given a time value string in the format 'HH:MM', this function parses the string and calculates the corresponding duration in minutes.
   * It returns the duration as a numerical value representing the total number of minutes.
   * USE-CASE: '02:00' -> (120 minutes)
   */

  // Split the time string into hours and minutes
  const parts = timeString.split(':');
  // Convert hours to minutes and add minutes
  return `${parseInt(parts[0]) * 60 + parseInt(parts[1])} minutes`;
};

export const getDayOfWeek = (date) => {
  // Using internationalization API to get the day name from Synfusion EJ2 react components
  const instance = new Internationalization();
  const dayOfWeek = instance.formatDate(date, { skeleton: 'E' });

  // 0 - Sunday, 1 - Monday, ..., 6 - Saturday
  const dayOfWeekNumber = moment(date).day();
  return { id: dayOfWeekNumber === 0 ? 7 : dayOfWeekNumber, name: dayOfWeek }; //returns {id: '1', name: 'Mon'}
};

export const getHourAndMinutes = (date, timeFormat) => {
  const instance = new Internationalization();
  // 24 hour format
  return instance.formatDate(date, {
    skeleton: timeFormat === 'HH:mm' ? 'Hm' : 'hm',
  });
};

export const formatWithMoment = (date, format) => {
  return moment(date, format).format(format);
};

export const dateComparator = (valueA, valueB) => {
  const date1 = new Date(valueA);
  const date2 = new Date(valueB);
  if (date1 === date2) return 0;
  return date1 > date2 ? 1 : -1;
};

export const calculateDates = (
  dayOfTheWeek,
  weekNumber,
  startTime,
  endTime,
  period,
) => {
  // Convert period startDate and endDate strings to Date objects
  let startDate = new Date(period.startDate);
  let endDate = new Date(period.endDate);

  // Validate that startDate is before endDate
  if (startDate > endDate) {
    throw new Error('Invalid period: startDate must be before endDate');
  }

  // Calculate the target date based on dayOfTheWeek and weekNumber
  let currentDate = new Date(startDate); // Start from the period startDate
  currentDate.setHours(0, 0, 0, 0); // Start at midnight to avoid time zone issues

  // Find the first occurrence of the dayOfTheWeek within the period
  let currentDay = currentDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  let daysToAdd = dayOfTheWeek - currentDay;
  if (daysToAdd < 0) {
    daysToAdd += 7; // Ensure daysToAdd is positive or zero
  }
  currentDate.setDate(currentDate.getDate() + daysToAdd);

  // Adjust for weekNumber
  if (weekNumber > 1) {
    currentDate.setDate(currentDate.getDate() + (weekNumber - 1) * 7);
  }

  // Format the start date
  let formattedStartDate = `${currentDate.toLocaleString('en-US', {
    month: 'long',
  })} ${currentDate.getDate()}, ${currentDate.getFullYear()} ${startTime}`;

  // Adjust end date if endTime is '00:00:00' or overlaps to the next day
  let formattedEndDate = `${currentDate.toLocaleString('en-US', {
    month: 'long',
  })} ${currentDate.getDate()}, ${currentDate.getFullYear()} ${endTime}`;
  let startHour = parseInt(startTime.split(':')[0]);
  let endHour = parseInt(endTime.split(':')[0]);

  // Check if endTime is '00:00:00' or overlaps to the next day
  if (
    endTime === '00:00:00' ||
    endTime === startTime ||
    endHour < startHour ||
    (endHour === startHour &&
      parseInt(endTime.split(':')[1]) < parseInt(startTime.split(':')[1]))
  ) {
    let nextDay = new Date(currentDate);
    nextDay.setDate(nextDay.getDate() + 1); // Move to the next day
    formattedEndDate = `${nextDay.toLocaleString('en-US', {
      month: 'long',
    })} ${nextDay.getDate()}, ${nextDay.getFullYear()} ${endTime}`;
  }
  // Return the formatted dates as Date objects
  return {
    startTime: new Date(formattedStartDate),
    endTime: new Date(formattedEndDate),
  };
};

export const addMinutesToDate = (inputDate, minutesToAdd) => {
  return moment(inputDate)
    .add(minutesToAdd, 'm')
    .toDate();
};

export const setTimeToDate = (inputDate, hour, minutes) => {
  return new Date(
    inputDate.getFullYear(),
    inputDate.getMonth(),
    inputDate.getDate(),
    hour,
    minutes,
    0,
  );
};

//compute duration hh:mm to minutes(int)
export const computeDurationStringInMinutes = (timeString) => {
  // Split the time string into hours and minutes
  const parts = timeString.split(':');
  // Convert hours to minutes and add minutes
  return parseInt(parts[0]) * 60 + parseInt(parts[1]);
};

//convert date to hh:mm
export const formatToHourMinute = (date) => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  // Combine into a formatted time string
  const timeFormatted = `${hours}:${minutes}`;
  return timeFormatted;
};

//convert minutes to hh:mm
export const formatIntToHourMinute = (number) => {
  const hourInt = Math.floor(number / 60);
  const minuteInt = number - hourInt * 60;
  const hours = String(hourInt).padStart(2, '0');
  const minutes = String(minuteInt).padStart(2, '0');
  // Combine into a formatted time string
  const timeFormatted = `${hours}:${minutes}`;
  return timeFormatted;
};

//check if date if between 2 dates
export const isDateInRange = (targetDate, startDate, endDate) => {
  const toDateOnly = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0); // strip time
    return d;
  };

  const target = toDateOnly(targetDate);
  const start = toDateOnly(startDate);
  const end = toDateOnly(endDate);

  return target >= start && target <= end;
};

export const getWeeksBetweenDates = (startDate, endDate) => {
  const diffInMilliseconds = endDate.getTime() - startDate.getTime();
  const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);
  return Math.ceil(diffInDays / 7);
};

export const getDateString = (dateValue) => {
  const paddedDatePart = dateValue
    .getDate()
    .toString()
    .padStart(2, '0');

  const paddedMonthPart = dateValue
    .getDate()
    .toString()
    .padStart(2, '0');

  const yearPart = dateValue.getFullYear().toString();

  return `${paddedDatePart}${paddedMonthPart}${yearPart}`;
};

export const convertToUTC = (value) => {
  if (!value) return null;
  // Create a new Date object in UTC
  var dateValue = new Date(value);
  const utcDate = new Date(
    Date.UTC(
      dateValue.getFullYear(),
      dateValue.getMonth(),
      dateValue.getDate(),
      dateValue.getHours(),
      dateValue.getMinutes(),
    ),
  );
  return utcDate;
};

export const formatGridDate = (params) => DateService.toUTCTime(params.value);

export const getBroadcastDate = (date) => {
  const hour = date.getHours();
  const broadcastDate = new Date(date);

  // If it's between 00:00 and 05:59, we want the previous day
  if (hour < 6) {
    broadcastDate.setDate(broadcastDate.getDate() - 1);
  }

  return broadcastDate;
};

/**
 * Converts a given date to its corresponding broadcast date.
 * Broadcast day starts at 6:00 AM, so this function shifts the date forward by 6 hours.
 *
 * @param {Date | string | moment.Moment} date - The date to convert. Can be a Date object, ISO string, or moment object.
 * @returns {Date} The broadcast date (shifted by +6 hours).
 */
export const toBroadcastDate = (date) => {
  return moment(date).add(6, 'hours').toDate();
}
